// Subscription-only gates share a visual language, with a distinct story for
// each feature. Keep credit-pack/model access gates on their two-path paywall.
export const PLUS_FEATURES = {
  voice: {
    title: 'Unlock voice mode\nwith Plus.',
    description: 'A conversation that flows. Just speak your mind, and hear your ideas come to life.',
    icon: 'waveform-lines',
    benefits: [
      ['microphone', 'Speak naturally', 'Ask a question. Talk through an idea.'],
      ['volume-high', 'Hear answers out loud', 'Keep the conversation going without typing.'],
      ['sliders', 'Make it your own', 'Choose the voice you want to talk with.'],
    ],
    action: 'Unlock voice mode',
    note: 'Voice uses credits from your balance.',
  },
  work: {
    title: 'From an idea\nto finished work.',
    description: 'Give your bigger tasks to an AI agent that can plan, research, and make things for you.',
    icon: 'briefcase',
    benefits: [
      ['list-check', 'Let it handle the steps', 'One request can become a complete project.'],
      ['magnifying-glass', 'Go deeper with research', 'Bring together information, tools, and files.'],
      ['file-lines', 'Get something you can use', 'Take away documents, code, and more.'],
    ],
    action: 'Unlock Work mode',
    note: 'Work uses credits from your balance.',
  },
  memory: {
    title: 'A little more you.\nIn every conversation.',
    description: 'Let Gab AI learn from your conversations, so you can spend less time explaining yourself.',
    icon: 'brain',
    benefits: [
      ['sparkles', 'Remember what matters', 'Build on the details you share over time.'],
      ['comment', 'Start with more context', 'Get replies informed by your preferences.'],
      ['sliders', 'Stay in control', 'Review memories and turn learning off anytime.'],
    ],
    action: 'Unlock memory learning',
  },
  bots: {
    title: 'The right bot.\nReady for you.',
    description: 'Explore Plus bots with their own specialties, personalities, and ways of helping.',
    icon: 'robot',
    benefits: [
      ['sparkles', 'Find a fresh perspective', 'Choose a bot that fits what you have in mind.'],
      ['comment', 'Get straight to the conversation', 'Start with its built-in focus and instructions.'],
      ['bookmark', 'Keep your favorites close', 'Return to the bots you enjoy using.'],
    ],
    action: 'Unlock Plus bots',
  },
  'create-bots': {
    title: 'Make a bot\nfor every idea.',
    description: 'Build more custom bots, each with a purpose and a personality of its own.',
    icon: 'robot',
    benefits: [
      ['pen', 'Give each bot a focus', 'Create helpers for different parts of your day.'],
      ['sliders', 'Shape how they respond', 'Set their instructions, tone, and approach.'],
      ['comment', 'Pick up where you left off', 'Come back whenever you need their help.'],
    ],
    action: 'Create more bots with Plus',
  },
  'favorite-bots': {
    title: 'More favorites.\nAlways close by.',
    description: 'Keep more of your favorite bots within easy reach, all in one place.',
    icon: 'bookmark',
    benefits: [
      ['robot', 'Room for more bots', 'Grow your collection beyond the free limit.'],
      ['bars', 'Find them in your sidebar', 'Spend less time searching for the right bot.'],
      ['comment', 'Jump back into a chat', 'Your go-to helpers are a click away.'],
    ],
    action: 'Save more bots with Plus',
  },
  'favorite-models': {
    title: 'Your go-to models.\nAll together.',
    description: 'Keep more favorite AI models close, ready for whatever you want to do next.',
    icon: 'bookmark',
    benefits: [
      ['layer-group', 'Make room for more', 'Save beyond the free favorites limit.'],
      ['bars', 'Keep them within reach', 'Find your saved models in the sidebar.'],
      ['sparkles', 'Choose what fits the task', 'Move easily between your favorite models.'],
    ],
    action: 'Save more models with Plus',
  },
  compare: {
    title: 'One question.\nMore perspectives.',
    description: 'Compare AI models side by side and find the answer that works best for you.',
    icon: 'columns-3',
    benefits: [
      ['comment', 'Ask once', 'Send the same prompt to multiple models.'],
      ['columns-3', 'See the differences', 'Read their answers alongside each other.'],
      ['lightbulb', 'Find your favorite approach', 'Compare writing, reasoning, and ideas.'],
    ],
    action: 'Unlock model comparison',
    note: 'Each model uses its usual credits.',
  },
  'search-web': {
    title: 'Bring the web\ninto the conversation.',
    description: 'Go beyond a model’s existing knowledge with information from the web.',
    icon: 'globe',
    benefits: [
      ['magnifying-glass', 'Look up what you need', 'Search without leaving your conversation.'],
      ['clock', 'Explore timely information', 'Find information about what is happening now.'],
      ['link', 'Keep researching', 'Use the results to ask your next question.'],
    ],
    action: 'Unlock web search',
    note: 'Web search uses credits from your balance.',
  },
  history: {
    title: 'Your conversations.\nBack in reach.',
    description: 'Restore Plus to reopen your older chats and read earlier messages. Your history is still here.',
    icon: 'clock-rotate-left',
    benefits: [
      ['comments', 'Revisit older chats', 'Return to the conversations you have saved.'],
      ['clock-rotate-left', 'Read the whole story', 'See earlier messages in your conversations.'],
      ['magnifying-glass', 'Rediscover an idea', 'Open older conversations from search.'],
    ],
    action: 'Restore Plus',
  },
  archive: {
    title: 'A fresh start.\nA quieter sidebar.',
    description: 'Archive all your chats in one step and make space for what comes next.',
    icon: 'box-archive',
    benefits: [
      ['layer-group', 'Tidy up in one step', 'Skip archiving each conversation by hand.'],
      ['box-archive', 'Keep your chats saved', 'Archiving moves them out of the way.'],
      ['arrow-rotate-left', 'Bring them back later', 'Restore archived chats when you need them.'],
    ],
    action: 'Unlock bulk archiving',
  },
  delete: {
    title: 'Make room\nfor a clean slate.',
    description: 'Manage your chat history with the option to delete all conversations at once.',
    icon: 'sliders',
    benefits: [
      ['layer-group', 'Manage chats together', 'Save time with a single bulk action.'],
      ['trash', 'Clear your conversations', 'Remove the chat history you no longer need.'],
      ['lock', 'Confirm before deleting', 'You stay in control of the final decision.'],
    ],
    action: 'Unlock bulk chat controls',
  },
  'read-aloud': {
    title: 'Give your answers\na voice.',
    description: 'Turn a written response into audio and listen at your own pace.',
    icon: 'volume-high',
    benefits: [
      ['headphones', 'Listen instead of reading', 'Hear a response spoken out loud.'],
      ['play', 'Control the playback', 'Pause and continue when you are ready.'],
      ['comment', 'Keep your place', 'The written answer stays in your chat.'],
    ],
    action: 'Unlock read aloud',
    note: 'Speech generation uses credits from your balance.',
  },
  model: {
    title: 'Find your next\nfavorite AI model.',
    description: 'Explore more AI models for a different approach to your questions, writing, and ideas.',
    icon: 'sparkles',
    benefits: [
      ['comment', 'A fresh way to think', 'Try a different perspective on your prompt.'],
      ['pen', 'Find your voice', 'Explore different styles of writing.'],
      ['sliders', 'Choose what works for you', 'Use the model that fits your task.'],
    ],
    action: 'Continue with Plus',
    note: 'Model usage is billed in credits.',
  },
  video: {
    title: 'An idea worth\nseeing in motion.',
    description: 'Turn your ideas into video with AI generation, right from your conversation.',
    icon: 'video',
    benefits: [
      ['pen', 'Describe your vision', 'Start with the scene you have in mind.'],
      ['video', 'Watch it take shape', 'Generate a video from your prompt.'],
      ['download', 'Keep what you create', 'Download your result when it is ready.'],
    ],
    action: 'Continue with Plus',
    note: 'Video generation uses credits from your balance.',
  },
  image: {
    title: 'Picture the idea.\nThen create it.',
    description: 'Explore more ways to bring your ideas to life with AI image generation.',
    icon: 'image',
    benefits: [
      ['pen', 'Start with your imagination', 'Describe the image you want to see.'],
      ['image', 'Explore a different look', 'Try new prompts and visual styles.'],
      ['download', 'Make it yours to keep', 'Download your finished image.'],
    ],
    action: 'Continue with Plus',
    note: 'Image generation uses credits from your balance.',
  },
  music: {
    title: 'Let your next idea\nbe a song.',
    description: 'Turn a sound, a mood, or an idea into music with AI generation.',
    icon: 'music',
    benefits: [
      ['pen', 'Set the direction', 'Describe the music you want to make.'],
      ['music', 'Hear it come together', 'Generate a track from your prompt.'],
      ['headphones', 'Listen to what you made', 'Play your result when it is ready.'],
    ],
    action: 'Continue with Plus',
    note: 'Music generation uses credits from your balance.',
  },
  api: {
    title: 'Build something\nwith Gab AI.',
    description: 'Bring AI into your own apps and workflows with access to the Gab AI API.',
    icon: 'code',
    benefits: [
      ['key', 'Create your API keys', 'Connect your projects to your account.'],
      ['layer-group', 'Explore different models', 'Choose the model that fits what you are building.'],
      ['code', 'Start with a familiar interface', 'Use the OpenAI-compatible API and code samples.'],
    ],
    action: 'Unlock API access',
    note: 'API requests use credits and are subject to plan limits.',
  },
  plus: {
    title: 'More possibilities.\nWith Plus.',
    description: 'Make space for bigger ideas with more ways to talk, create, and get things done.',
    icon: 'sparkles',
    benefits: [
      ['briefcase', 'Turn ideas into finished work', 'Let Work mode take on multi-step tasks.'],
      ['microphone', 'Talk it through', 'Have a conversation with voice mode.'],
      ['brain', 'Make it more personal', 'Let Gab AI learn from your conversations.'],
    ],
    action: 'Continue with Plus',
  },
};

export function getPlusFeature(feature) {
  const key = feature === 'work_mode' ? 'work' : feature;
  return Object.prototype.hasOwnProperty.call(PLUS_FEATURES, key) ? PLUS_FEATURES[key] : PLUS_FEATURES.plus;
}
